import React, { useState, useEffect } from 'react';
import {
  Users, Search, Eye, CheckCircle, XCircle,
  MoreVertical, Mail, Phone, MapPin, Building2, Award,
  Clock, AlertTriangle, Star, TrendingUp, TrendingDown, Gift, MessageSquare,
  UserCheck, Download, Trash2, LogIn, CalendarDays, Bell, Send, Plus, Lock, Pencil, UploadCloud
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Checkbox } from '../../components/ui/checkbox';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';

const normalizeTemplateKey = (value) =>
  String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

const getTemplateVariables = (template) => {
  const configured = Array.isArray(template?.variables) ? template.variables : [];
  const discovered = [];
  const source = `${template?.subject || ''}\n${template?.body || ''}`;
  const regex = /\{\{\s*([^{}]+?)\s*\}\}/g;
  let match;
  while ((match = regex.exec(source)) !== null) {
    const token = String(match[1] || '').trim();
    if (token) discovered.push(token);
  }
  return Array.from(new Set([...configured, ...discovered]));
};

const getMemberTemplateDefaults = (member) => {
  const contactName =
    member?.individual_name || member?.contact_name || member?.full_name || 'Member';
  const portalUrl = `${window.location.origin}/login`;
  return {
    contact_name: contactName,
    member_name: contactName,
    full_name: contactName,
    first_name: String(contactName).split(' ')[0] || 'Member',
    company_name: member?.company_name || '',
    email: member?.email || '',
    member_id: member?.member_id || '',
    tier: member?.tier || '',
    phone: member?.phone || '',
    city: member?.city || '',
    country: member?.country || '',
    portal_link: portalUrl,
    login_link: portalUrl,
    registration_link: `${window.location.origin}/register`,
    register_link: `${window.location.origin}/register`,
    share_social_link: `${window.location.origin}/share-social`,
    support_email: 'info@oneglobealliance.com',
    current_year: String(new Date().getFullYear()),
  };
};

const renderTemplateText = (text, variables) =>
  String(text || '').replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (placeholder, token) => {
    const exactValue = variables?.[token];
    if (exactValue !== undefined && exactValue !== '') return String(exactValue);
    const normalizedValue = Object.entries(variables || {}).find(
      ([key]) => normalizeTemplateKey(key) === normalizeTemplateKey(token)
    )?.[1];
    return normalizedValue !== undefined && normalizedValue !== ''
      ? String(normalizedValue)
      : placeholder;
  });

const getMissingTemplateVariables = (template, variables) =>
  getTemplateVariables(template).filter(
    (fieldName) => !String(variables?.[fieldName] || '').trim()
  );

const MemberStatusCard = ({ active, label, count, icon, onClick }) => (
  <button type="button" role="tab" aria-selected={active} onClick={onClick} className="rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2">
    <Card className={active ? 'h-full bg-gradient-to-br from-purple-500 to-purple-600 text-white' : 'h-full transition-colors hover:border-purple-300'}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className={active ? 'text-sm text-purple-100' : 'text-sm text-gray-500'}>{label}</p>
            <p className="text-2xl font-bold">{count}</p>
          </div>
          {icon}
        </div>
      </CardContent>
    </Card>
  </button>
);

const AdminMembers = () => {
  const [members, setMembers] = useState([]);
  const [expiringMembers, setExpiringMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memberStatusCounts, setMemberStatusCounts] = useState({});
  const [allMembersCount, setAllMembersCount] = useState(0);

  // Bulk selection
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkParams, setBulkParams] = useState({});

  // Impersonation
  const [showImpersonateDialog, setShowImpersonateDialog] = useState(false);
  const [impersonationData, setImpersonationData] = useState(null);

  // Expiry Management
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);
  const [expiryForm, setExpiryForm] = useState({
    start_date: '',
    expiry_date: ''
  });

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [availableTiers, setAvailableTiers] = useState([]);
  const [expiryFilter, setExpiryFilter] = useState(''); // 'expiring_30', 'expiring_60', 'expired'

  // Dialogs
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showValidateDialog, setShowValidateDialog] = useState(false);
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [showPointsDialog, setShowPointsDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  // Forms
  const [validationForm, setValidationForm] = useState({ status: 'approved', reason: '', query: '' });
  const [tierForm, setTierForm] = useState({ new_tier: '', reason: '' });
  const [pointsForm, setPointsForm] = useState({ points: 0, reason: '' });
  const [noteForm, setNoteForm] = useState({ note: '', note_type: 'general' });
  const [emailForm, setEmailForm] = useState({
    templateId: '',
    templateVariables: {},
  });

  // Restriction manage
  const [showAddRestrictionDialog, setShowAddRestrictionDialog] = useState(false);
  const [showEditRestrictionDialog, setShowEditRestrictionDialog] = useState(false);
  const [editingRestriction, setEditingRestriction] = useState(null);
  const [restrictionForm, setRestrictionForm] = useState({
    restriction_types: ['full'],
    reason: '',
    expires_at: ''
  });
  const [reasonOptions, setReasonOptions] = useState({});

  const { api, admin, hasPermission } = useAdminAuth();
  const canCreateTargets = hasPermission('target_members', 'create');
  const isSuperAdmin = admin?.role === 'super_admin';

  const RESTRICTION_TYPE_OPTIONS = [
    { value: 'full', label: 'Full Access Block' },
    { value: 'quotes', label: 'Quote Tool Block' },
    { value: 'events', label: 'Events Block' },
    { value: 'chat', label: 'Chat Block' },
    { value: 'ma', label: 'M&A Block' },
    { value: 'directory', label: 'Hide from Directory' },
  ];

  useEffect(() => {
    if (hasPermission('members', 'restrict')) {
      api.get('/api/admin/restrictions/reason-options')
        .then((res) => setReasonOptions(res.data?.reason_options || {}))
        .catch(() => setReasonOptions({
          full: ['Terms violation', 'Payment dispute', 'Other'],
          quotes: ['Spam/abuse', 'Policy violation', 'Other'],
          events: ['No-show history', 'Policy violation', 'Other'],
          chat: ['Inappropriate messaging', 'Spam', 'Other'],
          ma: ['Misuse of listings', 'Policy violation', 'Other'],
          directory: ['Privacy request', 'Profile violation', 'Other'],
        }));
    }
  }, [api, hasPermission]);

  useEffect(() => {
    fetchMembers();
  }, [search, statusFilter, tierFilter]);

  useEffect(() => {
    const dedupeTierNames = (tierNames = []) => {
      const seen = new Set();
      return tierNames
        .map((tierName) => String(tierName || '').trim())
        .filter(Boolean)
        .filter((tierName) => {
          const key = tierName.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    };

    const loadTierOptions = async () => {
      try {
        const response = await api.get('/api/admin/members/tiers/options');
        const normalized = dedupeTierNames(response.data?.tiers || []);
        if (normalized.length > 0) {
          setAvailableTiers(normalized);
          return;
        }
      } catch (error) {
        // Fall back to public active tiers endpoint when admin lacks tier-config access.
      }

      try {
        const response = await api.get('/api/tiers');
        const tiers = dedupeTierNames(
          (response.data || []).map(
            (tier) =>
              tier.display_name || tier.name || tier.tier_name || tier.id || ''
          )
        );
        setAvailableTiers(tiers);
      } catch (error) {
        setAvailableTiers([]);
      }
    };

    loadTierOptions();
  }, [api]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (tierFilter) params.append('tier', tierFilter);

      const queryString = params.toString();
      const response = await api.get(`/api/admin/members/${queryString ? '?' + queryString : ''}`);
      setMembers(response.data.members || []);
      setMemberStatusCounts(response.data.status_counts || {});
      setAllMembersCount(response.data.all_members_count ?? response.data.total ?? 0);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberDetails = async (memberId) => {
    try {
      const response = await api.get(`/api/admin/members/${memberId}`);
      setMemberDetails(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load member details');
    }
  };

  const handleValidate = async () => {
    try {
      await api.post(`/api/admin/members/${selectedMember.member_id}/validate`, {
        status: validationForm.status,
        rejection_reason: validationForm.reason,
        query_message: validationForm.query
      });
      toast.success(`Member ${validationForm.status}`);
      setShowValidateDialog(false);
      setValidationForm({ status: 'approved', reason: '', query: '' });
      fetchMembers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Validation failed'));
    }
  };

  const handleTierChange = async () => {
    if (!tierForm.new_tier || !tierForm.reason) {
      toast.error('Please select tier and provide reason');
      return;
    }
    try {
      await api.post(`/api/admin/members/${selectedMember.member_id}/tier-change`, tierForm);
      toast.success(`Tier changed to ${tierForm.new_tier}`);
      setShowTierDialog(false);
      setTierForm({ new_tier: '', reason: '' });
      fetchMembers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Tier change failed'));
    }
  };

  const handleGrantPoints = async () => {
    if (!pointsForm.points || !pointsForm.reason) {
      toast.error('Please enter points and reason');
      return;
    }
    try {
      await api.post(
        `/api/admin/members/${selectedMember.member_id}/grant-points?points=${pointsForm.points}&reason=${encodeURIComponent(pointsForm.reason)}`
      );
      toast.success(`${pointsForm.points} points granted`);
      setShowPointsDialog(false);
      setPointsForm({ points: 0, reason: '' });
      fetchMembers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to grant points'));
    }
  };

  const handleAddNote = async () => {
    if (!noteForm.note) {
      toast.error('Please enter a note');
      return;
    }
    try {
      await api.post('/api/admin/member-notes', {
        member_id: selectedMember.member_id,
        note: noteForm.note,
        note_type: noteForm.note_type
      });
      toast.success('Note added');
      setShowNoteDialog(false);
      setNoteForm({ note: '', note_type: 'general' });
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const openMemberEmailDialog = async (member) => {
    setSelectedMember(member);
    setEmailForm({ templateId: '', templateVariables: {} });
    setShowEmailDialog(true);
    if (emailTemplates.length > 0) return;

    try {
      setEmailTemplatesLoading(true);
      const response = await api.get('/api/admin/team/templates');
      setEmailTemplates(response.data?.templates || []);
    } catch (error) {
      toast.error('Failed to load email templates');
    } finally {
      setEmailTemplatesLoading(false);
    }
  };

  const handleMemberEmailTemplateSelection = (templateId) => {
    const template = emailTemplates.find((item) => item.template_id === templateId);
    const defaults = getMemberTemplateDefaults(selectedMember);
    const templateVariables = {};
    getTemplateVariables(template).forEach((fieldName) => {
      templateVariables[fieldName] = defaults[normalizeTemplateKey(fieldName)] || '';
    });
    setEmailForm({ templateId, templateVariables });
  };

  const handleSendMemberEmail = async () => {
    if (!selectedMember || !emailForm.templateId) {
      toast.error('Select an email template');
      return;
    }
    const template = emailTemplates.find(
      (item) => item.template_id === emailForm.templateId
    );
    const missingFields = getMissingTemplateVariables(
      template,
      emailForm.templateVariables
    );
    if (missingFields.length > 0) {
      toast.error(`Fill these fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setEmailSending(true);
      const response = await api.post(
        `/api/admin/members/${selectedMember.member_id}/send-template-email`,
        {
          template_id: emailForm.templateId,
          template_variables: emailForm.templateVariables,
        }
      );
      toast.success(response.data?.message || 'Email sent');
      const savedNote = response.data?.note;
      if (
        savedNote &&
        memberDetails?.member?.member_id === selectedMember.member_id
      ) {
        setMemberDetails((current) => ({
          ...current,
          notes: [savedNote, ...(current?.notes || [])],
        }));
      }
      setShowEmailDialog(false);
      setEmailForm({ templateId: '', templateVariables: {} });
    } catch (error) {
      toast.error(
        getErrorMessage(error.response?.data?.detail, 'Failed to send email')
      );
    } finally {
      setEmailSending(false);
    }
  };

  const handleAddAsTarget = async (member) => {
    try {
      const response = await api.post(`/api/admin/members/${member.member_id}/add-to-target`);
      toast.success(response.data?.message || 'Target member created');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to add member as target'));
    }
  };

  const getRestrictionTypes = (r) => r?.restriction_types || (r?.restriction_type ? [r.restriction_type] : []);
  const getReasonOptionsForTypes = (types) => {
    const seen = new Set();
    const options = [];
    for (const t of (types || [])) {
      for (const o of (reasonOptions[t] || [])) {
        if (!seen.has(o)) { seen.add(o); options.push(o); }
      }
    }
    const list = options.length ? options : ['Other'];
    return [...list.filter((o) => o.toLowerCase() !== 'other'), ...list.filter((o) => o.toLowerCase() === 'other')];
  };
  const toggleRestrictionType = (value) => {
    const current = restrictionForm.restriction_types || [];
    const next = current.includes(value) ? current.filter(t => t !== value) : [...current, value];
    setRestrictionForm({ ...restrictionForm, restriction_types: next.length ? next : ['full'] });
  };

  const handleAddRestriction = async () => {
    const mid = memberDetails?.member?.member_id;
    if (!mid || !restrictionForm.restriction_types?.length || !restrictionForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await api.post('/api/admin/restrictions', {
        member_id: mid,
        restriction_types: restrictionForm.restriction_types,
        reason: restrictionForm.reason,
        expires_at: restrictionForm.expires_at || null
      });
      toast.success('Restriction added');
      setShowAddRestrictionDialog(false);
      setRestrictionForm({ restriction_types: ['full'], reason: '', expires_at: '' });
      fetchMemberDetails(mid);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to add restriction'));
    }
  };

  const handleUpdateRestriction = async () => {
    if (!editingRestriction || !restrictionForm.restriction_types?.length || !restrictionForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await api.put(`/api/admin/restrictions/${editingRestriction.restriction_id}`, {
        restriction_types: restrictionForm.restriction_types,
        reason: restrictionForm.reason,
        expires_at: restrictionForm.expires_at || null
      });
      toast.success('Restriction updated');
      setShowEditRestrictionDialog(false);
      setEditingRestriction(null);
      setRestrictionForm({ restriction_types: ['full'], reason: '', expires_at: '' });
      fetchMemberDetails(memberDetails?.member?.member_id);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update restriction'));
    }
  };

  const handleRemoveRestriction = async (restrictionId) => {
    if (!window.confirm('Remove this restriction?')) return;
    try {
      await api.delete(`/api/admin/restrictions/${restrictionId}`);
      toast.success('Restriction removed');
      fetchMemberDetails(memberDetails?.member?.member_id);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to remove restriction'));
    }
  };

  const openEditRestriction = (r) => {
    setEditingRestriction(r);
    setRestrictionForm({
      restriction_types: getRestrictionTypes(r),
      reason: r.reason || '',
      expires_at: r.expires_at ? new Date(r.expires_at).toISOString().split('T')[0] : ''
    });
    setShowEditRestrictionDialog(true);
  };

  // Bulk Actions
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedMembers(members.map(m => m.member_id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleSelectMember = (memberId) => {
    if (selectedMembers.includes(memberId)) {
      setSelectedMembers(selectedMembers.filter(id => id !== memberId));
    } else {
      setSelectedMembers([...selectedMembers, memberId]);
    }
  };

  const handleBulkAction = async () => {
    if (selectedMembers.length === 0) {
      toast.error('No members selected');
      return;
    }

    try {
      const response = await api.post('/api/admin/members/bulk/action', {
        member_ids: selectedMembers,
        action: bulkAction,
        params: bulkParams
      });

      toast.success(response.data.message);
      setShowBulkDialog(false);
      setSelectedMembers([]);
      setBulkAction('');
      setBulkParams({});
      fetchMembers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Bulk action failed'));
    }
  };

  const handleExportSelected = async () => {
    try {
      const payload = selectedMembers.length > 0
        ? { member_ids: selectedMembers, export_all: false }
        : {
            member_ids: null,
            export_all: true,
            search: search || null,
            status: statusFilter || null,
            tier: tierFilter || null
          };
      const response = await api.post('/api/admin/members/bulk/export', payload);

      // Create CSV
      const data = response.data?.members ?? [];
      const headers = ['member_id', 'company_name', 'email', 'country', 'city', 'tier', 'status', 'reward_points', 'created_at'];
      const csvRows = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `members-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();

      toast.success(`Exported ${data.length} members`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Impersonation
  const handleImpersonate = async (memberId) => {
    try {
      const response = await api.post(`/api/admin/members/${memberId}/impersonate`);
      setImpersonationData(response.data);
      setShowImpersonateDialog(true);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to generate impersonation token'));
    }
  };

  const launchImpersonation = () => {
    if (impersonationData?.token) {
      // Store impersonation token and open member portal in new tab
      const url = `${window.location.origin}/?impersonate=${impersonationData.token}`;
      window.open(url, '_blank');
      setShowImpersonateDialog(false);
    }
  };

  // Expiry Management
  const handleUpdateExpiry = async () => {
    if (!selectedMember || !expiryForm.expiry_date) {
      toast.error('Please select an expiry date');
      return;
    }

    try {
      const response = await api.put(`/api/admin/members/${selectedMember.member_id}/expiry`, expiryForm);
      const updatedCount = response.data?.updated_members_count;
      toast.success(
        selectedMember.company_id && updatedCount
          ? `Membership expiry date updated for ${updatedCount} company member${updatedCount === 1 ? '' : 's'}`
          : 'Membership expiry date updated'
      );
      setShowExpiryDialog(false);
      fetchMembers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update expiry date'));
    }
  };

  const handleSendRenewalReminder = async (memberId) => {
    try {
      await api.post(`/api/admin/members/${memberId}/renewal-reminder`);
      toast.success('Renewal reminder sent (MOCK - logged to console)');
      fetchMembers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send reminder'));
    }
  };

  const handleExtendTrial = async (memberId) => {
    try {
      const reason = prompt('Enter reason for extending trial:');
      if (!reason) return;

      const days = parseInt(prompt('Enter number of days to extend (default: 15):') || '15');

      await api.post(`/api/admin/members/${memberId}/extend-trial?extension_days=${days}&reason=${encodeURIComponent(reason)}`);
      toast.success(`Trial extended by ${days} days`);
      fetchMembers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to extend trial'));
    }
  };

  const handleSendBulkReminders = async (days = 30) => {
    try {
      const response = await api.post(`/api/admin/members/send-bulk-reminders?days=${days}`);
      toast.success(`Sent ${response.data.sent_count} renewal reminders (MOCK)`);
      fetchMembers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send bulk reminders'));
    }
  };

  const handleSendBulkRemindersToSelected = async () => {
    if (selectedMembers.length === 0) {
      toast.error('No members selected');
      return;
    }

    try {
      // Send to selected members only
      const response = await api.post('/api/admin/members/bulk/send-reminders', {
        member_ids: selectedMembers
      });
      toast.success(`Sent ${response.data.sent_count} renewal reminders to selected members`);
      setSelectedMembers([]);
      fetchMembers();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send bulk reminders'));
    }
  };

  // Check if member is expiring soon
  const isExpiringSoon = (member) => {
    if (!member.membership_expiry_date) return false;
    const expiryDate = new Date(member.membership_expiry_date);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  };

  const isExpired = (member) => {
    if (!member.membership_expiry_date) return false;
    return new Date(member.membership_expiry_date) < new Date();
  };

  const getStatusBadge = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    const badges = {
      active: { bg: 'bg-green-100', text: 'text-green-700' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      suspended: { bg: 'bg-red-100', text: 'text-red-700' },
      rejected: { bg: 'bg-gray-100', text: 'text-gray-700' },
      'registered interest': { bg: 'bg-blue-100', text: 'text-blue-700' },
      'docs submitted': { bg: 'bg-amber-100', text: 'text-amber-700' },
      'verification complete': { bg: 'bg-emerald-100', text: 'text-emerald-700' }
    };
    return badges[normalized] || badges.pending;
  };

  const getTierBadge = (tier) => {
    const badges = {
      'Registered User': { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
      Silver: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
      Gold: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
      Platinum: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
      Founder: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' }
    };
    return badges[tier] || badges.Silver;
  };

  const isRegisteredUserTier = (tier) =>
    String(tier || '').trim().toLowerCase() === 'registered user';

  const tierOptions = availableTiers.length > 0
    ? availableTiers
    : Array.from(new Set((members || []).map((member) => member?.tier).filter(Boolean)));

  const defaultBulkTier =
    tierOptions.find((tier) => !isRegisteredUserTier(tier)) || tierOptions[0] || '';

  return (
    <div className="space-y-6" data-testid="admin-members">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Members Management</h1>
          <p className="text-gray-500">View, validate, and manage member accounts</p>
        </div>
        <div className="flex space-x-2">
          {selectedMembers.length > 0 && (
            <>
              <Badge className="bg-blue-100 text-blue-700 px-3 py-1">
                {selectedMembers.length} selected
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setBulkAction('tier_change');
                  setBulkParams({ new_tier: defaultBulkTier, reason: 'Bulk upgrade' });
                  setShowBulkDialog(true);
                }}
              >
                <TrendingUp size={16} className="mr-1" />
                Bulk Tier Change
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setBulkAction('grant_points');
                  setBulkParams({ points: 100, reason: 'Bonus' });
                  setShowBulkDialog(true);
                }}
              >
                <Gift size={16} className="mr-1" />
                Bulk Grant Points
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-amber-600 border-amber-300"
                onClick={() => handleSendBulkRemindersToSelected()}
              >
                <Bell size={16} className="mr-1" />
                Send Renewal to Selected
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportSelected}
              >
                <Download size={16} className="mr-1" />
                Export Selected
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedMembers([])}
              >
                <XCircle size={16} />
              </Button>
            </>
          )}
          {selectedMembers.length === 0 && (
            <>
              <Button
                variant="outline"
                className="text-amber-600 border-amber-300"
                onClick={() => handleSendBulkReminders(30)}
              >
                <Bell size={16} className="mr-2" />
                Send Renewal Reminders
              </Button>
              <Button variant="outline" onClick={handleExportSelected}>
                <Download size={16} className="mr-2" />
                Export All
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6" role="tablist" aria-label="Filter members by status">
        <MemberStatusCard active={!statusFilter} label="Show All" count={allMembersCount} icon={<Users size={24} className={!statusFilter ? 'text-purple-100' : 'text-blue-500'} />} onClick={() => setStatusFilter('')} />
        <MemberStatusCard active={statusFilter === 'pending'} label="Pending Validation" count={memberStatusCounts.pending || 0} icon={<Clock size={24} className={statusFilter === 'pending' ? 'text-purple-100' : 'text-yellow-500'} />} onClick={() => setStatusFilter('pending')} />
        <MemberStatusCard active={statusFilter === 'pending_verification'} label="Pending Verification" count={memberStatusCounts.pending_verification || 0} icon={<UserCheck size={24} className={statusFilter === 'pending_verification' ? 'text-purple-100' : 'text-amber-500'} />} onClick={() => setStatusFilter('pending_verification')} />
        <MemberStatusCard active={statusFilter === 'active'} label="Active Members" count={memberStatusCounts.active || 0} icon={<CheckCircle size={24} className={statusFilter === 'active' ? 'text-purple-100' : 'text-green-500'} />} onClick={() => setStatusFilter('active')} />
        <MemberStatusCard active={statusFilter === 'suspended'} label="Suspended" count={memberStatusCounts.suspended || 0} icon={<AlertTriangle size={24} className={statusFilter === 'suspended' ? 'text-purple-100' : 'text-red-500'} />} onClick={() => setStatusFilter('suspended')} />
        <MemberStatusCard active={statusFilter === 'rejected'} label="Rejected" count={memberStatusCounts.rejected || 0} icon={<XCircle size={24} className={statusFilter === 'rejected' ? 'text-purple-100' : 'text-red-500'} />} onClick={() => setStatusFilter('rejected')} />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="col-span-2">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search by company, name, email, or member ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              className="px-3 py-2 border rounded-md"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="pending_verification">Pending Verification</option>
              <option value="suspended">Suspended</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              className="px-3 py-2 border rounded-md"
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
            >
              <option value="">All Tiers</option>
              {tierOptions.map((tierName) => (
                <option key={tierName} value={tierName}>
                  {tierName}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-2 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={selectedMembers.length === members.length && members.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded"
                  />
                </th>
                <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Member</th>
                <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Company</th>
                <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Location</th>
                <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Tier</th>
                <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Source</th>
                <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Points</th>
                <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Expiry</th>
                <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase whitespace-nowrap">Last Reminder</th>
                <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Status</th>
                <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Joined</th>
                <th className="px-2 py-2 text-right text-[10px] font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="12" className="px-2 py-6 text-center text-gray-500">Loading...</td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan="12" className="px-2 py-6 text-center text-gray-500">No members found</td>
                </tr>
              ) : (
                members.map((member) => {
                  const statusText = member.status_display || member.status || 'pending';
                  const statusBadge = getStatusBadge(statusText);
                  const tierBadge = getTierBadge(member.tier);
                  const isSelected = selectedMembers.includes(member.member_id);
                  const expiringSoon = isExpiringSoon(member);
                  const expired = isExpired(member);
                  return (
                    <tr key={member.member_id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''} ${expired ? 'bg-red-50' : expiringSoon ? 'bg-amber-50' : ''}`}>
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectMember(member.member_id)}
                          className="w-4 h-4 rounded"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                            <span className="text-blue-600 font-semibold text-[10px]">
                              {(member.individual_name || member.company_name || '?')[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{member.individual_name || 'N/A'}</p>
                            <p className="text-[10px] text-gray-500">{member.member_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <div>
                          <p className="font-medium">{member.company_name}</p>
                          <p className="text-[10px] text-gray-500">{member.email}</p>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center text-gray-600">
                          <MapPin size={12} className="mr-1 shrink-0" />
                          {member.city ? `${member.city}, ` : ''}{member.country}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <Badge className={`${tierBadge.bg} ${tierBadge.text} border ${tierBadge.border} text-[10px]`}>
                          {member.tier || 'Silver'}
                        </Badge>
                      </td>
                      <td className="px-2 py-2">
                        <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px]">
                          {member.source || 'Website'}
                        </Badge>
                      </td>
                      <td className="px-2 py-2">
                        <span className="font-medium">{(member.reward_points || 0).toLocaleString()}</span>
                      </td>
                      <td className="px-2 py-2">
                        {member.membership_expiry_date ? (
                          <div className="flex items-center space-x-1">
                            {expired ? (
                              <Badge className="bg-red-100 text-red-700 text-[10px]">
                                <AlertTriangle size={10} className="mr-0.5" />
                                Expired
                              </Badge>
                            ) : expiringSoon ? (
                              <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                                <Clock size={10} className="mr-0.5" />
                                Expiring
                              </Badge>
                            ) : (
                              <span className="text-gray-600">
                                {new Date(member.membership_expiry_date).toLocaleDateString()}
                              </span>
                            )}
                            {member.renewal_reminder_sent && (
                              <Bell size={10} className="text-green-600 shrink-0" title="Reminder sent" />
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">Not set</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-gray-600 whitespace-nowrap">
                        {member.renewal_reminder_sent_at
                          ? new Date(member.renewal_reminder_sent_at).toLocaleDateString()
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-2 py-2">
                        <Badge className={`${statusBadge.bg} ${statusBadge.text} text-[10px]`}>
                          {statusText}
                        </Badge>
                      </td>
                      <td className="px-2 py-2 text-gray-500 whitespace-nowrap">
                        {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <MoreVertical size={14} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => fetchMemberDetails(member.member_id)}>
                              <Eye size={14} className="mr-2" /> View Details
                            </DropdownMenuItem>
                            {hasPermission('members', 'validate') && (
                              <DropdownMenuItem onClick={() => window.open(`/admin/members/${member.member_id}/documents`, '_blank', 'noopener,noreferrer')}>
                                <UploadCloud size={14} className="mr-2" /> Upload Documents
                              </DropdownMenuItem>
                            )}
                            {member.status === 'pending' && hasPermission('members', 'validate') && (
                              <DropdownMenuItem onClick={() => { setSelectedMember(member); setShowValidateDialog(true); }}>
                                <CheckCircle size={14} className="mr-2" /> Validate
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {hasPermission('members', 'edit') && (
                              <DropdownMenuItem
                                disabled={isRegisteredUserTier(member.tier) && !isSuperAdmin}
                                onClick={() => {
                                  if (isRegisteredUserTier(member.tier) && !isSuperAdmin) return;
                                  setSelectedMember(member);
                                  setTierForm({ new_tier: member.tier, reason: '' });
                                  setShowTierDialog(true);
                                }}
                              >
                                <Award size={14} className="mr-2" /> Change Tier
                              </DropdownMenuItem>
                            )}
                            {hasPermission('points', 'grant') && (
                              <DropdownMenuItem onClick={() => { setSelectedMember(member); setShowPointsDialog(true); }}>
                                <Gift size={14} className="mr-2" /> Grant Points
                              </DropdownMenuItem>
                            )}
                            {canCreateTargets && isRegisteredUserTier(member.tier) && (
                              <DropdownMenuItem onClick={() => handleAddAsTarget(member)}>
                                <Plus size={14} className="mr-2" /> Add as Target
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => {
                              setSelectedMember(member);
                              setExpiryForm({
                                start_date: member.membership_start_date || '',
                                expiry_date: member.membership_expiry_date || ''
                              });
                              setShowExpiryDialog(true);
                            }}>
                              <CalendarDays size={14} className="mr-2" /> Set Expiry Date
                            </DropdownMenuItem>
                            {member.membership_expiry_date && (
                              <DropdownMenuItem onClick={() => handleSendRenewalReminder(member.member_id)}>
                                <Send size={14} className="mr-2" /> Send Renewal Reminder
                              </DropdownMenuItem>
                            )}
                            {member.tier === 'free_trial' && (
                              <DropdownMenuItem onClick={() => handleExtendTrial(member.member_id)}>
                                <Clock size={14} className="mr-2" /> Extend Trial
                              </DropdownMenuItem>
                            )}
                            {hasPermission('members', 'edit') && (
                              <DropdownMenuItem onClick={() => openMemberEmailDialog(member)}>
                                <Mail size={14} className="mr-2" /> Send Email Template
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => { setSelectedMember(member); setShowNoteDialog(true); }}>
                              <MessageSquare size={14} className="mr-2" /> Add Note
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleImpersonate(member.member_id)} className="text-purple-600">
                              <LogIn size={14} className="mr-2" /> Login as Member
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Expiry Management Dialog */}
      <Dialog open={showExpiryDialog} onOpenChange={setShowExpiryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CalendarDays size={20} className="mr-2 text-blue-600" />
              Membership Expiry
            </DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Set membership dates for <strong>{selectedMember.company_name || selectedMember.individual_name}</strong>
                {selectedMember.company_id && (
                  <span className="block mt-1 text-amber-700">
                    This will update the same expiry date for all members of this company.
                  </span>
                )}
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Membership Start Date</Label>
                  <Input
                    type="date"
                    value={expiryForm.start_date ? expiryForm.start_date.split('T')[0] : ''}
                    onChange={(e) => setExpiryForm({ ...expiryForm, start_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Membership Expiry Date</Label>
                  <Input
                    type="date"
                    value={expiryForm.expiry_date ? expiryForm.expiry_date.split('T')[0] : ''}
                    onChange={(e) => setExpiryForm({ ...expiryForm, expiry_date: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              {selectedMember.membership_expiry_date && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <AlertTriangle size={14} className="inline mr-1" />
                    Current expiry: {new Date(selectedMember.membership_expiry_date).toLocaleDateString()}
                    {selectedMember.renewal_reminder_sent && (
                      <span className="ml-2 text-green-600">(Reminder sent)</span>
                    )}
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowExpiryDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateExpiry}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save Expiry Date
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Member Details Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Member Details</DialogTitle>
          </DialogHeader>
          {memberDetails && (
            <Tabs defaultValue="info">
              <TabsList>
                <TabsTrigger value="info">Information</TabsTrigger>
                <TabsTrigger value="payments">Payments ({memberDetails.payments?.length || 0})</TabsTrigger>
                <TabsTrigger value="invoices">Invoices ({memberDetails.invoices?.length || 0})</TabsTrigger>
                <TabsTrigger value="notes">Notes ({memberDetails.notes?.length || 0})</TabsTrigger>
                <TabsTrigger value="restrictions">Restrictions ({memberDetails.restrictions?.length || 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <Label className="text-gray-500">Member ID</Label>
                      <p className="font-mono">{memberDetails.member?.member_id}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Company Name</Label>
                      <p className="font-medium">{memberDetails.member?.company_name}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Contact Person</Label>
                      <p>{memberDetails.member?.individual_name}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Email</Label>
                      <p>{memberDetails.member?.email}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Phone</Label>
                      <p>{memberDetails.member?.phone || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-gray-500">Location</Label>
                      <p>{memberDetails.member?.city}, {memberDetails.member?.country}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Tier</Label>
                      <Badge className="ml-2">{memberDetails.member?.tier || 'Silver'}</Badge>
                    </div>
                    <div>
                      <Label className="text-gray-500">Reward Points</Label>
                      <p className="font-bold text-lg">{(memberDetails.member?.reward_points || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Status</Label>
                      <Badge className="ml-2">{memberDetails.member?.status}</Badge>
                    </div>
                    <div>
                      <Label className="text-gray-500">Joined</Label>
                      <p>{memberDetails.member?.created_at ? new Date(memberDetails.member.created_at).toLocaleString() : 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="payments">
                {memberDetails.payments?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No payments found</p>
                ) : (
                  <div className="space-y-2">
                    {memberDetails.payments?.map((payment, idx) => (
                      <div key={idx} className="p-3 border rounded-lg flex justify-between items-center">
                        <div>
                          <p className="font-medium">${payment.amount}</p>
                          <p className="text-sm text-gray-500">{payment.payment_id}</p>
                        </div>
                        <Badge>{payment.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="invoices">
                {memberDetails.invoices?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No invoices found</p>
                ) : (
                  <div className="space-y-2">
                    {memberDetails.invoices?.map((invoice, idx) => (
                      <div key={idx} className="p-3 border rounded-lg flex justify-between items-center">
                        <div>
                          <p className="font-medium">{invoice.invoice_id}</p>
                          <p className="text-sm text-gray-500">${invoice.total}</p>
                        </div>
                        <Badge>{invoice.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="notes">
                {memberDetails.notes?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No notes found</p>
                ) : (
                  <div className="space-y-2">
                    {memberDetails.notes?.map((note, idx) => (
                      <div
                        key={note.note_id || idx}
                        className={`rounded-lg border p-3 ${
                          note.note_type === 'email'
                            ? 'border-blue-200 bg-blue-50'
                            : 'border-gray-100 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium">{note.note}</p>
                            {note.note_type === 'email' && (
                              <div className="mt-2 flex flex-wrap gap-2 text-xs text-blue-800">
                                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                                  <Mail size={12} className="mr-1" />
                                  {note.email_template_name || 'Email template'}
                                </Badge>
                                <span>Recipient: {note.email_sent_to}</span>
                              </div>
                            )}
                          </div>
                          {note.note_type === 'email' && (
                            <Badge variant="outline" className="shrink-0 border-blue-300 text-blue-700">
                              Email sent
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {note.created_by_name} • {new Date(note.email_sent_at || note.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="restrictions">
                <div className="space-y-4">
                  {hasPermission('members', 'restrict') && (
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => {
                          setRestrictionForm({ restriction_types: ['full'], reason: '', expires_at: '' });
                          setShowAddRestrictionDialog(true);
                        }}
                      >
                        <Plus size={14} className="mr-1" />
                        Add Restriction
                      </Button>
                    </div>
                  )}
                  {memberDetails.restrictions?.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No active restrictions</p>
                  ) : (
                    <div className="space-y-2">
                      {memberDetails.restrictions?.map((restriction, idx) => {
                        const types = getRestrictionTypes(restriction);
                        const typeLabels = { full: 'Full Access Block', quotes: 'Quote Tool Block', events: 'Events Block', chat: 'Chat Block', ma: 'M&A Block', directory: 'Hide from Directory' };
                        return (
                          <div key={restriction.restriction_id || idx} className="p-3 bg-red-50 border border-red-200 rounded-lg flex justify-between items-start">
                            <div>
                              <p className="font-medium text-red-700">{types.map(t => typeLabels[t] || t).join(', ')}</p>
                              <p className="text-sm text-red-600">{restriction.reason}</p>
                              {restriction.expires_at && (
                                <p className="text-xs text-gray-500 mt-1">Expires: {new Date(restriction.expires_at).toLocaleDateString()}</p>
                              )}
                            </div>
                            {hasPermission('members', 'restrict') && (
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => openEditRestriction(restriction)}>
                                  <Pencil size={12} className="mr-1" /> Edit
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleRemoveRestriction(restriction.restriction_id)}>
                                  <Trash2 size={12} className="mr-1" /> Remove
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Member Email Dialog */}
      <Dialog
        open={showEmailDialog}
        onOpenChange={(open) => {
          setShowEmailDialog(open);
          if (!open) {
            setEmailForm({ templateId: '', templateVariables: {} });
          }
        }}
      >
        <DialogContent className="flex max-h-[88vh] max-w-2xl flex-col overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-6 pb-4 pt-6">
            <DialogTitle className="flex items-center">
              <Mail size={20} className="mr-2 text-blue-600" />
              Send Email Template
            </DialogTitle>
            <p className="text-sm text-gray-500">
              To {selectedMember?.individual_name || selectedMember?.company_name} ({selectedMember?.email})
            </p>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
            <div>
              <Label htmlFor="member-email-template">Email Template *</Label>
              <select
                id="member-email-template"
                className="mt-1 w-full rounded-md border px-3 py-2"
                value={emailForm.templateId}
                onChange={(event) =>
                  handleMemberEmailTemplateSelection(event.target.value)
                }
                disabled={emailTemplatesLoading}
              >
                <option value="">
                  {emailTemplatesLoading ? 'Loading templates...' : 'Select a template...'}
                </option>
                {emailTemplates.map((template) => (
                  <option key={template.template_id} value={template.template_id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            {(() => {
              const selectedTemplate = emailTemplates.find(
                (template) => template.template_id === emailForm.templateId
              );
              if (!selectedTemplate) return null;
              return (
                <>
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-medium uppercase text-gray-500">Preview</p>
                    <p className="mt-2 font-semibold text-gray-900">
                      {renderTemplateText(selectedTemplate.subject, emailForm.templateVariables)}
                    </p>
                    <p className="mt-3 max-h-48 overflow-y-auto whitespace-pre-wrap break-words text-sm text-gray-700">
                      {renderTemplateText(selectedTemplate.body, emailForm.templateVariables)}
                    </p>
                  </div>

                  {getTemplateVariables(selectedTemplate).length > 0 && (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {getTemplateVariables(selectedTemplate).map((fieldName) => (
                        <div key={fieldName}>
                          <Label htmlFor={`member-email-field-${fieldName}`}>
                            {fieldName} *
                          </Label>
                          <Input
                            id={`member-email-field-${fieldName}`}
                            className="mt-1"
                            value={emailForm.templateVariables[fieldName] || ''}
                            onChange={(event) =>
                              setEmailForm((current) => ({
                                ...current,
                                templateVariables: {
                                  ...current.templateVariables,
                                  [fieldName]: event.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div className="flex shrink-0 justify-end gap-2 border-t bg-white px-6 py-4">
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 text-white hover:bg-blue-700"
              onClick={handleSendMemberEmail}
              disabled={
                emailSending ||
                !emailForm.templateId ||
                getMissingTemplateVariables(
                  emailTemplates.find(
                    (template) => template.template_id === emailForm.templateId
                  ),
                  emailForm.templateVariables
                ).length > 0
              }
            >
              {emailSending ? (
                <>
                  <Clock size={16} className="mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Restriction Dialog */}
      <Dialog open={showAddRestrictionDialog} onOpenChange={setShowAddRestrictionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <Lock size={20} className="mr-2" />
              Add Restriction
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Member: <strong>{memberDetails?.member?.company_name}</strong> ({memberDetails?.member?.member_id})</p>
            <div>
              <Label>Restriction Type *</Label>
              <div className="mt-2 space-y-2 border rounded-md p-3 bg-gray-50">
                {RESTRICTION_TYPE_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`mem-rest-${opt.value}`}
                      checked={(restrictionForm.restriction_types || []).includes(opt.value)}
                      onCheckedChange={() => toggleRestrictionType(opt.value)}
                    />
                    <label htmlFor={`mem-rest-${opt.value}`} className="text-sm cursor-pointer">{opt.label}</label>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Reason *</Label>
              <select
                value={restrictionForm.reason}
                onChange={(e) => setRestrictionForm({ ...restrictionForm, reason: e.target.value })}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="">Select...</option>
                {getReasonOptionsForTypes(restrictionForm.restriction_types).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Expiry Date (optional)</Label>
              <Input type="date" value={restrictionForm.expires_at} onChange={(e) => setRestrictionForm({ ...restrictionForm, expires_at: e.target.value })} className="mt-1" />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAddRestrictionDialog(false)}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleAddRestriction}>
                <Lock size={16} className="mr-2" /> Apply Restriction
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Restriction Dialog */}
      <Dialog open={showEditRestrictionDialog} onOpenChange={setShowEditRestrictionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <Pencil size={20} className="mr-2" />
              Manage Restriction
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Restriction Type *</Label>
              <div className="mt-2 space-y-2 border rounded-md p-3 bg-gray-50">
                {RESTRICTION_TYPE_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`mem-edit-${opt.value}`}
                      checked={(restrictionForm.restriction_types || []).includes(opt.value)}
                      onCheckedChange={() => toggleRestrictionType(opt.value)}
                    />
                    <label htmlFor={`mem-edit-${opt.value}`} className="text-sm cursor-pointer">{opt.label}</label>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Reason *</Label>
              <select
                value={restrictionForm.reason}
                onChange={(e) => setRestrictionForm({ ...restrictionForm, reason: e.target.value })}
                className="w-full mt-1 px-3 py-2 border rounded-md"
              >
                <option value="">Select...</option>
                {getReasonOptionsForTypes(restrictionForm.restriction_types).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Expiry Date (optional)</Label>
              <Input type="date" value={restrictionForm.expires_at} onChange={(e) => setRestrictionForm({ ...restrictionForm, expires_at: e.target.value })} className="mt-1" />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { setShowEditRestrictionDialog(false); setEditingRestriction(null); }}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleUpdateRestriction}>
                <Lock size={16} className="mr-2" /> Apply Restriction
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Validate Dialog */}
      <Dialog open={showValidateDialog} onOpenChange={setShowValidateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Validate Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Validate registration for <strong>{selectedMember?.company_name}</strong>
            </p>
            <div>
              <Label>Decision</Label>
              <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={validationForm.status}
                onChange={(e) => setValidationForm({ ...validationForm, status: e.target.value })}
              >
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
                <option value="pending_info">Request More Info</option>
              </select>
            </div>
            {validationForm.status === 'rejected' && (
              <div>
                <Label>Rejection Reason *</Label>
                <Textarea
                  value={validationForm.reason}
                  onChange={(e) => setValidationForm({ ...validationForm, reason: e.target.value })}
                  placeholder="Explain why the application is being rejected..."
                  rows={3}
                />
              </div>
            )}
            {validationForm.status === 'pending_info' && (
              <div>
                <Label>Query Message *</Label>
                <Textarea
                  value={validationForm.query}
                  onChange={(e) => setValidationForm({ ...validationForm, query: e.target.value })}
                  placeholder="What additional information do you need?"
                  rows={3}
                />
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowValidateDialog(false)}>Cancel</Button>
              <Button onClick={handleValidate} className="bg-blue-600 hover:bg-blue-700 text-white">
                Submit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tier Change Dialog */}
      <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Membership Tier</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Current tier: <Badge>{selectedMember?.tier || 'Silver'}</Badge>
            </p>
            <div>
              <Label>New Tier *</Label>
              <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={tierForm.new_tier}
                onChange={(e) => setTierForm({ ...tierForm, new_tier: e.target.value })}
              >
                <option value="">Select tier</option>
                {tierOptions.map((tierName) => (
                  <option key={tierName} value={tierName}>
                    {tierName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Reason *</Label>
              <Textarea
                value={tierForm.reason}
                onChange={(e) => setTierForm({ ...tierForm, reason: e.target.value })}
                placeholder="Reason for tier change..."
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowTierDialog(false)}>Cancel</Button>
              <Button onClick={handleTierChange} className="bg-blue-600 hover:bg-blue-700 text-white">
                Change Tier
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Grant Points Dialog */}
      <Dialog open={showPointsDialog} onOpenChange={setShowPointsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Points</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Current points: <strong>{(selectedMember?.reward_points || 0).toLocaleString()}</strong>
            </p>
            <div>
              <Label>Points to Grant *</Label>
              <Input
                type="number"
                value={pointsForm.points}
                onChange={(e) => setPointsForm({ ...pointsForm, points: parseInt(e.target.value) || 0 })}
                placeholder="Enter points"
              />
            </div>
            <div>
              <Label>Reason *</Label>
              <Textarea
                value={pointsForm.reason}
                onChange={(e) => setPointsForm({ ...pointsForm, reason: e.target.value })}
                placeholder="Reason for granting points..."
                rows={2}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowPointsDialog(false)}>Cancel</Button>
              <Button onClick={handleGrantPoints} className="bg-green-600 hover:bg-green-700 text-white">
                <Gift size={16} className="mr-2" />
                Grant Points
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Internal Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Note Type</Label>
              <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={noteForm.note_type}
                onChange={(e) => setNoteForm({ ...noteForm, note_type: e.target.value })}
              >
                <option value="general">General</option>
                <option value="follow_up">Follow Up</option>
                <option value="issue">Issue</option>
                <option value="verification">Verification</option>
              </select>
            </div>
            <div>
              <Label>Note *</Label>
              <Textarea
                value={noteForm.note}
                onChange={(e) => setNoteForm({ ...noteForm, note: e.target.value })}
                placeholder="Enter note..."
                rows={4}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button>
              <Button onClick={handleAddNote} className="bg-blue-600 hover:bg-blue-700 text-white">
                Add Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Action - {selectedMembers.length} Members</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {bulkAction === 'tier_change' && (
              <>
                <div>
                  <Label>New Tier</Label>
                  <select
                    value={bulkParams.new_tier || ''}
                    onChange={(e) => setBulkParams({ ...bulkParams, new_tier: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                  >
                    <option value="">Select tier</option>
                    {tierOptions.map((tierName) => (
                      <option key={tierName} value={tierName}>
                        {tierName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Reason</Label>
                  <Input
                    value={bulkParams.reason || ''}
                    onChange={(e) => setBulkParams({ ...bulkParams, reason: e.target.value })}
                    placeholder="Reason for tier change"
                    className="mt-1"
                  />
                </div>
              </>
            )}
            {bulkAction === 'grant_points' && (
              <>
                <div>
                  <Label>Points to Grant</Label>
                  <Input
                    type="number"
                    value={bulkParams.points || 0}
                    onChange={(e) => setBulkParams({ ...bulkParams, points: parseInt(e.target.value) })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Reason</Label>
                  <Input
                    value={bulkParams.reason || ''}
                    onChange={(e) => setBulkParams({ ...bulkParams, reason: e.target.value })}
                    placeholder="Reason for points grant"
                    className="mt-1"
                  />
                </div>
              </>
            )}
            {bulkAction === 'change_status' && (
              <div>
                <Label>New Status</Label>
                <select
                  value={bulkParams.status || ''}
                  onChange={(e) => setBulkParams({ ...bulkParams, status: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            )}
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
              <AlertTriangle size={16} className="inline mr-2" />
              This action will affect {selectedMembers.length} members.
            </div>
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
              <Button onClick={handleBulkAction} className="bg-blue-600 hover:bg-blue-700 text-white">
                Apply to All
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Impersonation Dialog */}
      <Dialog open={showImpersonateDialog} onOpenChange={setShowImpersonateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center text-purple-600">
              <LogIn size={20} className="mr-2" />
              Login as Member
            </DialogTitle>
          </DialogHeader>
          {impersonationData && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="font-medium">{impersonationData.member?.company_name}</p>
                <p className="text-sm text-gray-600">{impersonationData.member?.email}</p>
                <p className="text-xs text-gray-500 mt-1">{impersonationData.member?.member_id}</p>
              </div>

              <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
                <AlertTriangle size={16} className="inline mr-2" />
                <strong>Warning:</strong> {impersonationData.warning}
              </div>

              <div className="text-sm text-gray-600">
                <p><strong>Token expires in:</strong> {impersonationData.expires_in}</p>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowImpersonateDialog(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={launchImpersonation}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <LogIn size={16} className="mr-2" />
                  Open Portal as Member
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMembers;
